import React, { useEffect, useRef, useState } from "react";
import { whyContent, audienceStats } from "../data/mock";
import { Separator } from "./ui/separator";
import { Users, GraduationCap, Heart } from "lucide-react";

const statIcons = [Users, GraduationCap, Heart];

const useInView = (options = {}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.unobserve(entry.target);
      }
    }, { threshold: 0.15, ...options });

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [ref, isVisible];
};

const CountUp = ({ target, isVisible }) => {
  const [count, setCount] = useState(0);
  const match = target.match(/^(\d+)(.*)$/);
  const numericTarget = match ? parseInt(match[1], 10) : 0;
  const suffix = match ? match[2] : "";

  useEffect(() => {
    if (!isVisible || !numericTarget) return;
    const duration = 1800;
    const startTime = performance.now();

    const animate = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * numericTarget));
      if (progress < 1) requestAnimationFrame(animate);
    };

    requestAnimationFrame(animate);
  }, [isVisible, numericTarget]);

  if (!match) return <>{target}</>;
  return <>{count}{suffix}</>;
};

export const AboutSection = () => {
  const [labelRef, labelVisible] = useInView();
  const [leftRef, leftVisible] = useInView();
  const [rightRef, rightVisible] = useInView();
  const [walkerRef, walkerVisible] = useInView();

  return (
    <section id="about" className="py-16 md:py-[120px]" style={{ background: "#ffffff" }}>
      <div className="max-w-[87.5rem] mx-auto px-10">
        {/* Section Label */}
        <span
          ref={labelRef}
          className="inline-block text-[#6b7280] text-xs font-medium uppercase tracking-[0.2em] mb-6 transition-all duration-700"
          style={{
            fontFamily: "'Inter', Arial, sans-serif",
            opacity: labelVisible ? 1 : 0,
            transform: labelVisible ? "translateY(0)" : "translateY(12px)",
          }}
        >
          The Mission
        </span>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20">
          {/* Left - Why */}
          <div
            ref={leftRef}
            className="transition-all duration-700"
            style={{
              opacity: leftVisible ? 1 : 0,
              transform: leftVisible ? "translateY(0)" : "translateY(40px)",
            }}
          >
            <h2
              className="text-[#FE3A80] uppercase leading-[0.85] mb-6 md:mb-10"
              style={{
                fontFamily: "'Montserrat', Arial, sans-serif",
                fontWeight: 900,
                fontSize: "clamp(2rem, 1.5rem + 2vw, 3rem)",
              }}
            >
              {whyContent.title}
            </h2>
            <div className="space-y-6">
              {whyContent.paragraphs.map((p, i) => (
                <p
                  key={i}
                  className="text-[#1f2937] text-base md:text-lg font-medium leading-relaxed transition-all duration-700"
                  style={{
                    fontFamily: "'Inter', Arial, sans-serif",
                    opacity: leftVisible ? 1 : 0,
                    transform: leftVisible ? "translateY(0)" : "translateY(20px)",
                    transitionDelay: `${(i + 1) * 150}ms`,
                  }}
                >
                  {p}
                </p>
              ))}
            </div>
          </div>

          {/* Right - Audience Stats */}
          <div
            ref={rightRef}
            className="flex flex-col justify-center transition-all duration-700"
            style={{
              opacity: rightVisible ? 1 : 0,
              transform: rightVisible ? "translateY(0)" : "translateY(40px)",
              transitionDelay: "200ms",
            }}
          >
            <div className="space-y-0">
              {audienceStats.map((stat, i) => {
                const Icon = statIcons[i];
                return (
                  <div
                    key={i}
                    className="transition-all duration-700"
                    style={{
                      opacity: rightVisible ? 1 : 0,
                      transform: rightVisible ? "translateX(0)" : "translateX(30px)",
                      transitionDelay: `${(i + 1) * 200 + 200}ms`,
                    }}
                  >
                    <div className="flex items-start gap-5 py-8 px-5 -mx-5 cursor-default transition-all duration-500 group bg-[#FE3A80] hover:bg-[#e8306f] hover:scale-[1.02] hover:shadow-lg hover:shadow-[#FE3A80]/20">
                      <div className="w-10 h-10 rounded-full border border-white/30 flex items-center justify-center flex-shrink-0 mt-1 transition-all duration-300 group-hover:bg-white/20 group-hover:scale-110 group-hover:rotate-6">
                        <Icon size={18} className="text-white" />
                      </div>
                      <div>
                        <p
                          className="text-white text-3xl md:text-4xl font-bold mb-1 transition-transform duration-300 group-hover:translate-x-1"
                          style={{ fontFamily: "'Montserrat', Arial, sans-serif", fontWeight: 900 }}
                        >
                          <CountUp target={stat.value} isVisible={rightVisible} />
                        </p>
                        <p
                          className="text-white/90 text-sm font-medium uppercase tracking-wider"
                          style={{ fontFamily: "'Inter', Arial, sans-serif" }}
                        >
                          {stat.label}
                        </p>
                        {stat.sublabel && (
                          <p
                            className="text-white/60 text-xs mt-1"
                            style={{ fontFamily: "'Inter', Arial, sans-serif" }}
                          >
                            {stat.sublabel}
                          </p>
                        )}
                      </div>
                    </div>
                    {i < audienceStats.length - 1 && (
                      <div className="h-[1px] bg-[#e5e7eb] -mx-5" />
                    )}
                  </div>
                );
              })}
            </div>
            <p
              className="mt-8 text-[#6b7280] text-sm font-medium leading-relaxed transition-all duration-700"
              style={{
                fontFamily: "'Inter', Arial, sans-serif",
                opacity: rightVisible ? 1 : 0,
                transitionDelay: "1000ms",
              }}
            >
              This is a high-trust, values-driven audience that actively supports education, leadership development, and community engagement.
            </p>
          </div>
        </div>

      </div>

      {/* Walker Ottawa — Event Founder */}
      <div className="mt-16 md:mt-24" style={{ background: "#ffffff" }}>
        {/* Mobile layout — centered, logo-first */}
        <div
          className="block lg:hidden px-6 py-16"
          style={{ borderTop: "1px solid #e5e7eb" }}
        >
          <div className="text-center">
            <img
              src="/walker-ottawa-new-logo.png"
              alt="Walker Ottawa"
              className="w-64 h-auto mx-auto mb-5"
            />
            <span
              className="inline-block text-[10px] font-medium uppercase tracking-[0.2em] mb-4"
              style={{ fontFamily: "'Inter', Arial, sans-serif", color: "#6b7280" }}
            >
              Event Founder
            </span>
            <h2
              className="text-[#FE3A80] uppercase leading-[0.85] mb-4"
              style={{
                fontFamily: "'Montserrat', Arial, sans-serif",
                fontWeight: 900,
                fontSize: "1.75rem",
              }}
            >
              Walker Ottawa
            </h2>
            <p
              className="text-[#1f2937] text-sm font-medium leading-relaxed mb-6 mx-auto"
              style={{ fontFamily: "'Inter', Arial, sans-serif", maxWidth: "22rem" }}
            >
              Walker Real Estate Group with RE/MAX is a community-driven company that helps clients buy and sell homes while remaining deeply committed to giving back to the community in meaningful ways. They believe strong communities are built by supporting local initiatives, creating opportunities for connection, and uplifting the next generation.
            </p>
            <a
              href="https://walkerottawa.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center w-full px-8 py-3 text-sm font-semibold uppercase no-underline"
              style={{
                fontFamily: "'Montserrat', Arial, sans-serif",
                letterSpacing: "0.1em",
                color: "#FE3A80",
                background: "transparent",
                border: "1px solid #FE3A80",
                maxWidth: "20rem",
              }}
            >
              Visit Walker Ottawa
            </a>
          </div>
        </div>

        {/* Desktop layout — two column */}
        <div
          ref={walkerRef}
          className="hidden lg:block max-w-[87.5rem] mx-auto px-10 py-[120px]"
          style={{ borderTop: "1px solid #e5e7eb" }}
        >
          <span
            className="inline-block text-xs text-[#6b7280] font-medium uppercase tracking-[0.2em] mb-6"
            style={{
              fontFamily: "'Inter', Arial, sans-serif",
              opacity: walkerVisible ? 1 : 0,
              transform: walkerVisible ? "translateY(0)" : "translateY(12px)",
              transition: "opacity 0.7s ease-out, transform 0.7s ease-out",
            }}
          >
            Event Founder
          </span>

          <div className="grid grid-cols-2 gap-20 items-center">
            <div
              style={{
                opacity: walkerVisible ? 1 : 0,
                transform: walkerVisible ? "translateY(0)" : "translateY(40px)",
                transition: "opacity 0.7s ease-out 0.1s, transform 0.7s ease-out 0.1s",
              }}
            >
              <h2
                className="text-[#FE3A80] uppercase leading-[0.85] mb-8"
                style={{
                  fontFamily: "'Montserrat', Arial, sans-serif",
                  fontWeight: 900,
                  fontSize: "clamp(2rem, 1.5rem + 2vw, 3rem)",
                }}
              >
                Walker Ottawa
              </h2>
              <p
                className="text-[#1f2937] text-lg font-medium leading-relaxed mb-8"
                style={{ fontFamily: "'Inter', Arial, sans-serif" }}
              >
                Walker Real Estate Group with RE/MAX is a community-driven company that helps clients buy and sell homes while remaining deeply committed to giving back to the community in meaningful ways. They believe strong communities are built by supporting local initiatives, creating opportunities for connection, and uplifting the next generation.
              </p>
              <a
                href="https://walkerottawa.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-8 py-3.5 text-sm font-semibold uppercase cursor-pointer transition-all duration-300 hover:scale-[1.03] active:scale-[0.98] no-underline"
                style={{
                  fontFamily: "'Montserrat', Arial, sans-serif",
                  letterSpacing: "0.1em",
                  color: "#FE3A80",
                  background: "transparent",
                  border: "1px solid #FE3A80",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#FE3A80";
                  e.currentTarget.style.color = "#ffffff";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.color = "#FE3A80";
                }}
              >
                Visit Walker Ottawa
              </a>
            </div>

            <div
              className="flex items-center justify-center"
              style={{
                opacity: walkerVisible ? 1 : 0,
                transform: walkerVisible ? "translateY(0)" : "translateY(40px)",
                transition: "opacity 0.7s ease-out 0.3s, transform 0.7s ease-out 0.3s",
              }}
            >
              <img
                src="/walker-ottawa-new-logo.png"
                alt="Walker Ottawa"
                className="w-72 h-auto"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
